import type * as React from 'react';

import type { BadgeProps, IconDefinition, OverlayProps } from '@gitbook/spine-react';

import type { PopoverPlacement } from '../Popover';

export type PaletteAnchorRef = React.RefObject<HTMLElement | null>;

type AsyncMaybe<T> = Promise<T> | T;

/**
 * State of a palette visible in the DOM.
 */
export interface VisiblePalette {
    /**
     * Defintion of the palette to render.
     */
    palette: PaletteDef;

    /**
     * Ref to an inline element where the palette should be positioned arround.
     */
    anchorRef: PaletteAnchorRef;

    /**
     * Ref to an input that is linked to the palette.
     */
    inputRef?: React.RefObject<HTMLElement | null> | undefined;

    /**
     * Unique identifier for the palette.
     */
    id: string;
}

/**
 * Instance of a commands palette.
 */
export interface PaletteDef {
    /**
     * Mark the palette as static.
     * It allows to search in submenus.
     */
    static?: boolean;

    /**
     * Placeholder for a search input.
     * If not defined, no search input is rendered.
     */
    search?: { placeholder?: string; initialValue?: string };

    /**
     * Placeholder when the palette is empty.
     */
    placeholder?: string;

    /**
     * Query for filtering commands.
     */
    query: PaletteQueryState;

    /**
     * Groups of commands binded to this palette.
     */
    groups: PaletteGroupDef[];

    /**
     * Options to configure position of the palette.
     */
    position?: PalettePositionOptions;

    /**
     * Whether focus should be restored to the trigger when the palette closes.
     */
    restoreFocus?: OverlayProps['restoreFocus'];

    /**
     * Constrain the palette to the available space next to its anchor so a long list
     * scrolls internally instead of overflowing and being repositioned over the anchor.
     * Opt-in: only palettes whose anchor must stay visible (e.g. a search input) set this.
     */
    constrainHeight?: boolean;
}

type PalettePositionAlignment = 'start' | 'end';

/**
 * Options to configure position of the palette.
 */
export interface PalettePositionOptions {
    /**
     * Preferred placement of the palette.
     */
    preferredPlacement?: PopoverPlacement;

    /**
     * Preferred alignment of the palette.
     */
    preferredAlignment?: PalettePositionAlignment;
}

/**
 * Query for filtering/searching commands.
 */
export interface PaletteQueryState {
    text: string;
}

/**
 * Connector to a group of commands.
 */
export interface PaletteGroupDef {
    key: string;

    /**
     * Optional title shown on top of the group
     */
    title?: string;

    /**
     * Commands bind to this group.
     * If a function is provided, it will be called to get the list of commands.
     * The function can do any async operation by throwing a promise.
     */
    commands: ((query: string) => PaletteCommandDef[]) | PaletteCommandDef[];
}

/**
 * Function executed when command is called.
 */
export type PaletteCommandTrigger = () => AsyncMaybe<void | PaletteDef>;

export enum PaletteCommandType {
    RADIO = 0,
    CHECKBOX = 1,
    SWITCH = 2,
    CUSTOM = 3,
}

type PaletteCommandBase = {
    /**
     * Unique identifier for the command.
     */
    key: string;

    /**
     * Presentation for the command.
     */
    title: string;

    /**
     * Titles of the parents.
     */
    parentTitles?: string[];

    /**
     * Disable a palette command
     */
    disabled?: boolean;

    /**
     * The command is an upgrade action
     */
    upgrade?: boolean;

    /**
     * Display a badge trailing the title
     */
    badge?: BadgeProps;

    /** Icon component displayed on the left of the title */
    icon?: IconDefinition | undefined;

    /**
     * One-line text displayed under the title.
     * A function can be used to compute the subtitle lazily.
     */
    subtitle?: string | (() => string);

    /**
     * Whether the title and subtitle text should be truncated.
     * @default true
     */
    truncate?: boolean;

    /** Size of the command */
    size?: 'medium' | 'large';

    /**
     * Keywords to be indexed for autocomplete.
     * (not displayed)
     */
    keywords?: string[];

    /**
     * Pins the command ahead of fuzzy search results when rendered in a menu.
     */
    filterPriority?: number;

    /**
     * Optional suffix element displayed on the right side of the command.
     */
    suffix?: React.ReactNode;

    /**
     * Optional className to apply to the command's container.
     */
    className?: string;

    /**
     * Override the automatic sibling-icon spacer alignment.
     * Pass `false` only for rows that should intentionally opt out of left-alignment
     * with icon-bearing siblings (e.g. full-width custom rows).
     * Omit to let the menu infer alignment automatically.
     */
    hasIconSibling?: boolean;

    /**
     * Visual style of the command.
     * - 'default': standard palette item styling.
     * - 'outline': displays a border when idle; on hover/focus the border takes the neutral-ui color.
     * - 'danger': destructive action styling.
     * @default 'default'
     */
    kind?: 'default' | 'outline' | 'danger';

    /**
     * Added as data-testid.
     */
    testId?: string;
};

type PaletteSelectableCommandBase = PaletteCommandBase & {
    type: PaletteCommandType.RADIO | PaletteCommandType.CHECKBOX;

    /**
     * If true or false, a selection indicator is displayed on the left of the icon / title.
     */
    selected: boolean;

    /**
     * Callback executed when the command is run.
     */
    trigger: PaletteCommandTrigger;
    descendant?: () => PaletteDef;
    enabled?: undefined;
};

type PaletteCheckboxCommand = PaletteSelectableCommandBase & {
    type: PaletteCommandType.CHECKBOX;

    /**
     * Whether the palette should close after this checkbox command runs.
     * @default true
     */
    closeOnActionEnd?: boolean;
};

type PaletteRadioCommand = PaletteSelectableCommandBase & {
    type: PaletteCommandType.RADIO;
    closeOnActionEnd?: undefined;
};

type PaletteSwitchCommand = PaletteCommandBase & {
    type: PaletteCommandType.SWITCH;

    /**
     * If true or false, a switch button is
     * displayed on the left of the icon / title.
     */
    enabled: boolean;

    /**
     * Callback executed when the command is run.
     */
    trigger: PaletteCommandTrigger;
    descendant?: undefined;
    selected?: undefined;
};

/**
 * Alert-style custom command displayed in palette menus.
 */
export type PaletteCustomCommand = PaletteCommandBase & {
    type: PaletteCommandType.CUSTOM;
    enabled: boolean;
    trigger: PaletteCommandTrigger;
    descendant?: undefined;
    selected?: undefined;
};

type PaletteDescendantCommand = PaletteCommandBase & {
    /**
     * Callback to render a sub-palette.
     * The callback will be called when the user hovers the command.
     */
    descendant: () => PaletteDef;
    trigger?: PaletteCommandTrigger;
    selected?: undefined;
    enabled?: undefined;
    type?: undefined;
    /**
     * Optional keyboard shortcut to display on the right side of the command.
     * Only available for commands without a type (no radio/checkbox/switch).
     */
    keyboardShortcut?: string;
};

type PaletteActionCommand = PaletteCommandBase & {
    descendant?: () => PaletteDef;
    trigger: PaletteCommandTrigger;
    selected?: undefined;
    enabled?: undefined;
    type?: undefined;
    /**
     * Optional keyboard shortcut to display on the right side of the command.
     * Only available for commands without a type (no radio/checkbox/switch).
     */
    keyboardShortcut?: string;
};

/**
 * Definition for a command.
 */
export type PaletteCommandDef =
    | PaletteCheckboxCommand
    | PaletteRadioCommand
    | PaletteSwitchCommand
    | PaletteCustomCommand
    | PaletteDescendantCommand
    | PaletteActionCommand;

export interface ComputedCommandsSection {
    group: PaletteGroupDef;
    priority: number;
    data: PaletteCommandDef[];
}
